import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import { isNull, round } from "lodash";
import { useGridApiContext } from "@mui/x-data-grid";
import { MenuItem, Select, TextField, Checkbox } from "@mui/material";
import {
  NOTE_TYPE_VALUE,
  MenuProps,
  DropdownSx,
  multiplierMappings,
  isAdjQtyValid,
} from "../../../utils";
import AutoCompleteInput from "../../../components/Inputs/AutoCompleteInput";
import AddNoteOption from "../AddNoteOption";
import { useAddNoteMutation } from "../../../services/notesApi";
import {
  NOTE_TYPES,
  setNoteSaveType,
  resetNoteSaveType,
  NOTE_SAVE_TYPE,
} from "../../../store/notesSlice";
import {
  showNotifier,
  setPrevAdjQty,
  setAdjQtyError,
} from "../../../store/globalSlice";

const CustomEditComponent = React.memo((props) => {
  const {
    fieldType = "select",
    disabled,
    id,
    value,
    field,
    getProcessedRow,
    noteType = "",
    options = [],
    default: defaults = "",
    restrictEdit = false,
  } = props;
  const apiRef = useGridApiContext();
  const dispatch = useDispatch();
  const [notesLoader, setNotesLoader] = React.useState(false);
  const user = useSelector((store) => store.global.user);
  const store = useSelector((store) => store.global.store);
  const adjQtyError = useSelector((store) => store.global.adjQtyError);
  const prevAdjQty = useSelector((store) => store.global.prevAdjQty);
  const noteSaveType = useSelector((store) => store.notes.saveType);
  const [addNote] = useAddNoteMutation();

  const handleValueChange = (event) => {
    let newValue = event.target.value;
    if (event.target.name === "core/nonCore") {
      const currentValue = !isNull(newValue) ? Number(newValue) : null;
      newValue = currentValue;
      if (currentValue > 100 || isNaN(currentValue)) {
        return event.preventDefault();
      }
    }
    if (event.target.name === "days") {
      const currentValue = !isNull(newValue) ? Number(newValue) : null;
      newValue = currentValue;
      if (currentValue > 365 || isNaN(currentValue)) {
        return event.preventDefault();
      }
    }
    if (event.target.name === "adjQty") {
      if (!prevAdjQty) {
        dispatch(setPrevAdjQty(value));
      }
      if (adjQtyError) {
        dispatch(setAdjQtyError(null));
      }
      const currentValue = !isNull(newValue) ? Number(newValue) : null;
      newValue = currentValue;
      if (currentValue > 9999 || isNaN(currentValue)) {
        return event.preventDefault();
      }
      if (!isAdjQtyValid(currentValue)) {
        dispatch(
          showNotifier({
            severity: "error",
            message: `Quantity value of ${currentValue} is not allowed`,
          })
        );
        dispatch(setAdjQtyError(id));
      }
    }
    if (event.target.type === "checkbox") {
      newValue = event.target.checked;
    }
    apiRef.current.setEditCellValue({ id, field, value: newValue });
    getProcessedRow({ id, [field]: newValue }, field);
    if (event.target.type !== "text") {
      apiRef.current.stopCellEditMode({ id, field });
    }
    return true;
  };

  const autoCompleteChange = React.useCallback(
    (event, selectedValue = {}) => {
      if (selectedValue?.id) {
        apiRef.current.setEditCellValue({ id, field, value: selectedValue.id });
        getProcessedRow({ id, [field]: selectedValue.id }, field);
      } else if (selectedValue?.title) {
        const selectedOption = event.target.value || selectedValue.title;
        apiRef.current.setEditCellValue({ id, field, value: selectedOption });
        getProcessedRow({ id, [field]: selectedOption }, field);
      } else {
        apiRef.current.setEditCellValue({ id, field, value: "" });
        getProcessedRow({ id, [field]: "" }, field);
      }
      apiRef.current.stopCellEditMode({ id, field });
    },
    [apiRef, getProcessedRow, id, field]
  );

  const addNewNoteHandler = async (newValue) => {
    setNotesLoader(true);
    await addNote({
      notesType:
        noteType === NOTE_TYPES[1]
          ? NOTE_TYPE_VALUE.retailer
          : NOTE_TYPE_VALUE.planner,
      notes: newValue?.title?.trim(),
      createdBy: user.email,
      storeId: noteSaveType === NOTE_SAVE_TYPE[0] ? store.id : null,
    })
      .then(({ data }) => {
        const currentSelection = { ...newValue, id: data.NotesId };
        options.push(currentSelection);
        apiRef.current.setEditCellValue({
          id,
          field,
          value: currentSelection.id,
        });
        getProcessedRow({ id, [field]: currentSelection.id }, field);
        apiRef.current.stopCellEditMode({ id: field });
      })
      .finally(() => {
        setNotesLoader(false);
      });
  };

  const handleNoteTypeSaveChange = React.useCallback(
    (event) => {
      dispatch(setNoteSaveType());
      event.stopPropagation();
    },
    [dispatch]
  );

  const handleNoteDialogClose = React.useCallback(() => {
    dispatch(resetNoteSaveType());
  }, [dispatch]);

  switch (fieldType) {
    case "number":
      return (
        <TextField
          fullWidth
          size="small"
          margin="dense"
          label=""
          name="core/nonCore"
          autoFocus
          value={value}
          onChange={handleValueChange}
          inputProps={{ min: 0, max: 100 }}
          InputProps={{ style: { height: "100%" } }}
          onBlur={(event) => {
            const value = event.target.value;
            if (value === "") {
              apiRef.current.setEditCellValue({ id, field, value: null });
              getProcessedRow({ id, [field]: null }, field);
            } else {
              apiRef.current.setEditCellValue({
                id,
                field,
                value: round(value, 2),
              });
              getProcessedRow({ id, [field]: round(value, 2) }, field);
            }
          }}
        />
      );

    case "days":
      return (
        <TextField
          fullWidth
          size="small"
          label=""
          name="days"
          autoFocus
          value={value}
          onChange={handleValueChange}
          inputProps={{ min: 0, max: 365 }}
          InputProps={{ style: { height: "100%" } }}
          onKeyDown={(e) => {
            if (e.key === ".") {
              e.preventDefault();
            }
          }}
          onBlur={(event) => {
            const value = event.target.value;
            if (value === "") {
              apiRef.current.setEditCellValue({ id, field, value: null });
              getProcessedRow({ id, [field]: null }, field);
            } else {
              apiRef.current.setEditCellValue({ id, field, value });
              getProcessedRow({ id, [field]: value }, field);
            }
          }}
        />
      );

    case "adjQty":
      return (
        <TextField
          fullWidth
          size="small"
          label=""
          name="adjQty"
          autoFocus
          value={value}
          onChange={handleValueChange}
          inputProps={{
            min: 0,
            max: 9999,
            style: {
              backgroundColor: adjQtyError ? "rgba(255, 0, 0, 0.7)" : "inherit",
            },
          }}
          InputProps={{ style: { height: "100%" } }}
          onKeyDown={(e) => {
            if (e.key === ".") {
              e.preventDefault();
            }
          }}
          onBlur={(event) => {
            const value = event.target.value;
            if (value === "") {
              apiRef.current.setEditCellValue({ id, field, value: 0 });
              getProcessedRow({ id, [field]: 0 }, field);
            } else {
              apiRef.current.setEditCellValue({ id, field, value });
              getProcessedRow({ id, [field]: value }, field);
            }
          }}
        />
      );

    case "select":
      return (
        <Select
          fullWidth
          margintop="0"
          size="small"
          label=""
          autoFocus
          defaultValue={!defaults ? "" : defaults}
          value={!value ? "" : value}
          onChange={handleValueChange}
          MenuProps={MenuProps}
        >
          <MenuItem value="">None</MenuItem>
          {options.map((option) => (
            <MenuItem sx={DropdownSx} key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      );

    case "multiplier":
      return (
        <Select
          fullWidth
          margintop="0"
          size="small"
          label=""
          autoFocus
          defaultValue={!defaults ? "" : defaults}
          value={!value ? "" : value}
          onChange={handleValueChange}
          MenuProps={MenuProps}
          sx={{ maxWidth: 200, margin: 0 }}
        >
          {options.map((option) => (
            <MenuItem sx={DropdownSx} key={option} value={option}>
              {multiplierMappings[option]}
            </MenuItem>
          ))}
        </Select>
      );

    case "autoComplete":
      return (
        <AutoCompleteInput
          showAddOption={!restrictEdit}
          addOptionRenderer={(params) => <AddNoteOption inputValue={params} />}
          selectOnFocus
          clearOnBlur
          handleHomeEndKeys
          sx={{ width: 200, margin: 0 }}
          loading={notesLoader}
          onSelect={autoCompleteChange}
          value={value}
          optionList={options}
          updateList={addNewNoteHandler}
          noteSaveType={noteSaveType}
          onNoteTypeSaveChange={handleNoteTypeSaveChange}
          onDialogClose={handleNoteDialogClose}
        />
      );

    case "checkbox":
      return (
        <Checkbox
          disabled={disabled}
          checked={Boolean(value)}
          onChange={handleValueChange}
          inputProps={{ "aria-label": "controlled" }}
          sx={{ width: "auto", padding: "0rem", mx: "10px" }}
        />
      );

    default:
      return null;
  }
});

export default CustomEditComponent;
